import { useCallback, useMemo, useState } from 'react';

import type { GridApi } from 'ag-grid-community';
import { LoaderCircleIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { useGetBreedsQuery } from '@/app/services/lookups';
import { Button } from '@/components/ui/button';
import { ConfirmProvider, useConfirm } from '@/components/ui/confirm-dialog';
import {
  type DonorMismatchData,
  DonorMismatchDialog,
} from '@/pages/bulk-donor-scheduling/components/donor-mismatch-dialog';
import {
  type BulkDonorScheduleValidationResponse,
  type UpsertFarbroBulkDonorScheduleRequest,
  useUpsertFarbroBulkDonorSchedules,
  useValidateFarbroBulkDonorSchedules,
  type ValidateFarbroBulkDonorScheduleRequest,
  ValidationMessageLevel,
} from '@/services/bulk-donor-schedule';
import { isAbortError } from '@/services/progress-stream';

import { BulkDonorScheduleGrid } from './components/bulk-donor-schedule-grid';
import { UnsavedChangesGuard } from './components/unsaved-changes-guard';
import { useFarbroBulkDonorScheduleStore } from './farbro/hooks/use-farbro-bulk-donor-schedule-store';
import { useFarbroColumns } from './farbro/hooks/use-farbro-columns';
import { parseFarbroRoster } from './farbro/utils/farbro-roster-parser';
import {
  type FarbroBulkDonorScheduleInput,
  FarbroBulkDonorScheduleRowsSchema,
} from './farbro/utils/schema';
import { type Messages } from './hooks/use-bulk-donor-schedule-store';
import { useNotifications } from './hooks/use-notifications';

const normalizeBreedValue = (value: string) => value.trim().toLowerCase();

type PendingDonorMismatch = {
  rowIndex: number;
  donorName: string;
  mismatchData: DonorMismatchData;
};

type FarbroDonorResolution = NonNullable<
  FarbroBulkDonorScheduleInput['donorResolution']
>;

export function FarbroBulkDonorSchedulePage() {
  return (
    <ConfirmProvider>
      <FarbroBulkDonorSchedulePageContent />
    </ConfirmProvider>
  );
}

function FarbroBulkDonorSchedulePageContent() {
  const { t } = useTranslation();
  const confirm = useConfirm();

  const [state, dispatch] = useFarbroBulkDonorScheduleStore();
  const { data: bovineBreeds } = useGetBreedsQuery({});

  const [api, setApi] = useState<GridApi<FarbroBulkDonorScheduleInput>>();
  const [isGridEditing, setGridEditing] = useState(false);
  const [isFileProcessing, setFileProcessing] = useState(false);
  const [donorResolutions, setDonorResolutions] = useState<
    Record<number, FarbroDonorResolution>
  >({});
  const [pendingDonorMismatches, setPendingDonorMismatches] = useState<
    PendingDonorMismatch[]
  >([]);
  const [mismatchDialog, setMismatchDialog] = useState<{
    visible: boolean;
    rowIndex: number | null;
    donorName: string;
    mismatchData: DonorMismatchData | null;
  }>({
    visible: false,
    rowIndex: null,
    donorName: '',
    mismatchData: null,
  });

  const notifications = useNotifications();

  const validBovineBreedValues = useMemo(() => {
    const values = new Set<string>();
    for (const breed of bovineBreeds || []) {
      if (breed?.code) {
        values.add(normalizeBreedValue(breed.code));
      }
      if (breed?.name) {
        values.add(normalizeBreedValue(breed.name));
      }
    }
    return values;
  }, [bovineBreeds]);

  const columns = useFarbroColumns();

  const validateSchedule = useValidateFarbroBulkDonorSchedules();
  const upsertSchedulesMutation = useUpsertFarbroBulkDonorSchedules();

  const handleValidate = useCallback(async () => {
    if (!api) {
      return;
    }

    dispatch({ type: 'client_validating' });
    api.stopEditing();
    setPendingDonorMismatches([]);
    setMismatchDialog({
      visible: false,
      rowIndex: null,
      donorName: '',
      mismatchData: null,
    });

    const rows = state.rows.map((row) => {
      if (!row || Object.values(row).filter((v) => !!v).length === 0) {
        return undefined;
      }
      return row;
    });

    const rowsResult = FarbroBulkDonorScheduleRowsSchema.safeParse(rows);

    const inputs = rowsResult.data?.filter(
      (row): row is FarbroBulkDonorScheduleInput => row !== null,
    );

    if (!rowsResult.success || !inputs?.length) {
      const errors: Record<string, string> = {};

      const colId = api
        .getColumns()
        ?.find((c) => c.getColDef().field)
        ?.getColDef().field;
      if (!inputs?.length && colId) {
        errors[`${0}.${colId}`] = 'At least one valid row is required';
      }

      if (rowsResult.error?.issues) {
        for (const issue of rowsResult.error.issues) {
          const rowIndex = issue.path[0] as string;
          const colId = issue.path[1] as string;
          errors[`${rowIndex}.${colId}`] = issue.message;
        }
      }

      dispatch({ type: 'client_validated', errors });
      notifications.validationErrors();
      return;
    }

    if (bovineBreeds?.length) {
      const breedErrors: Record<string, string> = {};
      rowsResult.data.forEach((row, rowIndex) => {
        if (!row) {
          return;
        }

        const donorBreed = row.donorBreed?.trim();
        if (
          donorBreed &&
          !validBovineBreedValues.has(normalizeBreedValue(donorBreed))
        ) {
          breedErrors[`${rowIndex}.donorBreed`] =
            'Breed must be a valid bovine breed code or name';
        }
      });

      if (Object.keys(breedErrors).length > 0) {
        dispatch({ type: 'client_validated', errors: breedErrors });
        notifications.validationErrors();
        return;
      }
    }

    dispatch({ type: 'client_validated', errors: undefined });

    try {
      const variables: ValidateFarbroBulkDonorScheduleRequest = {
        inputs: rowsResult.data,
      };

      const result: BulkDonorScheduleValidationResponse =
        await validateSchedule.mutateAsync(variables);

      let messages: Messages | undefined = undefined;
      let errors: Record<string, string> | undefined = undefined;

      for (const validation of result.fields) {
        const field = validation.field;
        if (!messages) {
          messages = {};
        }
        messages[field] = {
          level: ValidationMessageLevel.MESSAGE,
          message: validation.message,
        };
      }

      const unresolvedDonorMismatches: PendingDonorMismatch[] = [];
      for (const validation of result.inputs) {
        const rowIndex = validation.index.toString();
        const colId = validation.field;

        if (
          validation.correctedValue &&
          validation.level === ValidationMessageLevel.MESSAGE
        ) {
          dispatch((state) => {
            const updatedRow = {
              ...state.rows[validation.index],
              [colId]: validation.correctedValue,
            };
            return {
              type: 'set_row',
              rowIndex: validation.index,
              data: updatedRow,
            };
          });
        }

        if (validation.level === ValidationMessageLevel.ERROR) {
          if (!errors) {
            errors = {};
          }
          errors[`${rowIndex}.${colId}`] = validation.message;
        } else {
          if (!messages) {
            messages = {};
          }
          messages[`${rowIndex}.${colId}`] = {
            level: validation.level,
            message: validation.message,
            suggestions: validation.suggestions || undefined,
          };

          if (
            validation.level === ValidationMessageLevel.WARNING &&
            validation.mismatchData &&
            !donorResolutions[validation.index]
          ) {
            const row = state.rows[validation.index];
            unresolvedDonorMismatches.push({
              rowIndex: validation.index,
              donorName: row?.donorNameToMatch || '',
              mismatchData: validation.mismatchData,
            });
          }
        }
      }

      if (errors) {
        setPendingDonorMismatches([]);
        notifications.validationErrors();
      } else if (unresolvedDonorMismatches.length > 0) {
        setPendingDonorMismatches(unresolvedDonorMismatches);
        const mismatch = unresolvedDonorMismatches[0];
        setMismatchDialog({
          visible: true,
          rowIndex: mismatch.rowIndex,
          donorName: mismatch.donorName,
          mismatchData: mismatch.mismatchData,
        });
      } else {
        setPendingDonorMismatches([]);
        notifications.validationSuccessful();
      }

      dispatch({ type: 'server_validated', errors, messages });
    } catch (error) {
      if (isAbortError(error)) {
        dispatch({ type: 'request_cancelled' });
        return;
      }
      notifications.validationServerError(error);
      dispatch({ type: 'server_error' });
      console.error('Error while validating data', error);
    }
  }, [
    api,
    dispatch,
    notifications,
    state.rows,
    validateSchedule,
    bovineBreeds,
    donorResolutions,
    validBovineBreedValues,
  ]);

  const handleUpload = useCallback(async () => {
    if (!api) {
      return;
    }

    dispatch({ type: 'uploading' });

    try {
      const parsedRows = FarbroBulkDonorScheduleRowsSchema.parse(state.rows);
      const rowsWithIndexes = parsedRows.flatMap((row, rowIndex) =>
        row
          ? [
              {
                row: {
                  ...row,
                  donorResolution:
                    donorResolutions[rowIndex] ?? row.donorResolution,
                },
                rowIndex,
              },
            ]
          : [],
      );

      const variables: UpsertFarbroBulkDonorScheduleRequest = {
        inputs: rowsWithIndexes.map(({ row }) => row),
      };

      const result = await upsertSchedulesMutation.mutateAsync(variables);
      const warningRowIndexes = new Set(
        result.manualFollowUpInputIndexes.flatMap((inputIndex) => {
          const rowIndex = rowsWithIndexes[inputIndex]?.rowIndex;
          return rowIndex === undefined ? [] : [rowIndex];
        }),
      );

      notifications.processingSuccessful({
        donorSchedulesProcessed: result.donorSchedulesProcessed,
        soSchedulesCreated: result.soSchedulesCreated,
        chuteSchedulesCreated: result.chuteSchedulesCreated,
        successfulRows: result.successfulInputIndexes.length,
        manualFollowUpRows: result.manualFollowUpInputIndexes.length,
      });
      dispatch({ type: 'success', warningRowIndexes });
    } catch (error) {
      if (isAbortError(error)) {
        dispatch({ type: 'request_cancelled' });
        return;
      }
      notifications.uploadServerError(error);
      dispatch({ type: 'server_error' });
      console.error('Error while uploading data', error);
    }
  }, [
    api,
    dispatch,
    donorResolutions,
    notifications,
    state.rows,
    upsertSchedulesMutation,
  ]);

  const handleRowChanged = useCallback(
    (rowIndex: number, data: FarbroBulkDonorScheduleInput) => {
      dispatch({ type: 'set_row', rowIndex, data });
      setDonorResolutions((current) => {
        const next = { ...current };
        delete next[rowIndex];
        return next;
      });
      setPendingDonorMismatches([]);
      setMismatchDialog({
        visible: false,
        rowIndex: null,
        donorName: '',
        mismatchData: null,
      });
    },
    [dispatch],
  );

  const resolveCurrentDonorMismatch = useCallback(
    (action: FarbroDonorResolution) => {
      if (mismatchDialog.rowIndex === null || !mismatchDialog.mismatchData) {
        return;
      }

      const existingDonorId = mismatchDialog.mismatchData.existingDonorId;
      if (action === 'USE_EXISTING' && !existingDonorId) {
        return;
      }

      setDonorResolutions((current) => ({
        ...current,
        [mismatchDialog.rowIndex!]: action,
      }));

      const remaining = pendingDonorMismatches.filter(
        (mismatch) => mismatch.rowIndex !== mismatchDialog.rowIndex,
      );
      setPendingDonorMismatches(remaining);
      const nextMismatch = remaining[0];
      setMismatchDialog(
        nextMismatch
          ? {
              visible: true,
              rowIndex: nextMismatch.rowIndex,
              donorName: nextMismatch.donorName,
              mismatchData: nextMismatch.mismatchData,
            }
          : {
              visible: false,
              rowIndex: null,
              donorName: '',
              mismatchData: null,
            },
      );

      if (!nextMismatch) {
        notifications.validationSuccessful();
      }
    },
    [mismatchDialog, notifications, pendingDonorMismatches],
  );

  const handleUseExistingData = useCallback(() => {
    resolveCurrentDonorMismatch('USE_EXISTING');
  }, [resolveCurrentDonorMismatch]);

  const handleCreateNewDonor = useCallback(() => {
    resolveCurrentDonorMismatch('CREATE_NEW');
  }, [resolveCurrentDonorMismatch]);

  const handleCancelMismatch = useCallback(() => {
    setPendingDonorMismatches([]);
    dispatch({ type: 'idle' });
    setMismatchDialog({
      visible: false,
      rowIndex: null,
      donorName: '',
      mismatchData: null,
    });
  }, [dispatch]);

  const processFileUpload = useCallback(
    async (file: File) => {
      setFileProcessing(true);
      try {
        const roster = await parseFarbroRoster(file);

        if (roster.rows.length === 0) {
          notifications.fileProcessingError(
            'No valid data found in Excel file',
          );
          return;
        }

        const loadRoster = () => {
          setDonorResolutions({});
          setPendingDonorMismatches([]);
          dispatch({
            type: 'upload_from_excel',
            data: roster.rows,
            sourceRows: roster.sourceRows,
          });
          notifications.fileUploadSuccess(
            `Successfully loaded ${roster.rows.length} rows`,
          );
        };
        loadRoster();
      } catch (error) {
        console.error('Error processing Excel file:', error);
        notifications.fileProcessingError(
          error instanceof Error
            ? error.message
            : 'Failed to process Excel file',
        );
      } finally {
        setFileProcessing(false);
      }
    },
    [dispatch, notifications],
  );

  const showFilePicker = useCallback(() => {
    const fileInput = document.createElement('input');
    fileInput.type = 'file';
    fileInput.accept = '.xlsx';
    fileInput.onchange = () => {
      const file = fileInput.files?.[0];
      if (file) {
        void processFileUpload(file);
      }
    };
    fileInput.click();
  }, [processFileUpload]);

  const handleUploadClick = useCallback(() => {
    const hasData = state.rows.some(
      (row) =>
        row &&
        Object.values(row).some((val) => val !== undefined && val !== ''),
    );

    if (!hasData) {
      showFilePicker();
      return;
    }

    confirm({
      title: t('bulkDonorScheduling.uploadConfirmHeader'),
      description: t('bulkDonorScheduling.uploadConfirmMessage'),
      confirmLabel: t('common.yes'),
      cancelLabel: t('common.no'),
      onConfirm: showFilePicker,
    });
  }, [confirm, showFilePicker, state.rows, t]);

  return (
    <div className="h-[calc(100%-4rem)]">
      <UnsavedChangesGuard state={state} />

      <DonorMismatchDialog
        visible={mismatchDialog.visible}
        mismatchData={mismatchDialog.mismatchData}
        donorName={mismatchDialog.donorName}
        onUseExisting={handleUseExistingData}
        onCreateNew={handleCreateNewDonor}
        onCancel={handleCancelMismatch}
      />

      <div className="mb-4 flex justify-end">
        <div className="flex flex-wrap gap-2">
          {state.type === 'idle' && (
            <Button
              size="xl"
              disabled={state.loading || isFileProcessing || isGridEditing}
              onClick={handleUploadClick}
            >
              {isFileProcessing ? (
                <>
                  <LoaderCircleIcon className="animate-spin" />
                  {t('bulkDonorScheduling.Validating')}
                </>
              ) : (
                t('bulkDonorScheduling.Upload')
              )}
            </Button>
          )}
          {state.type === 'server_validated' ||
          state.type === 'uploading' ||
          state.type === 'success' ? (
            <Button
              size="xl"
              variant="success"
              className="flex-grow justify-center"
              disabled={
                state.loading ||
                state.type === 'success' ||
                isGridEditing ||
                pendingDonorMismatches.length > 0
              }
              onClick={handleUpload}
            >
              {state.loading ? (
                <>
                  <LoaderCircleIcon className="animate-spin" />
                  {t('bulkDonorScheduling.Uploading')}
                </>
              ) : (
                t('bulkDonorScheduling.Upload')
              )}
            </Button>
          ) : (
            <Button
              size="xl"
              className="justify-center"
              disabled={state.loading || isGridEditing}
              onClick={handleValidate}
            >
              {state.loading ? (
                <>
                  <LoaderCircleIcon className="animate-spin" />
                  {t('bulkDonorScheduling.Validating')}
                </>
              ) : (
                t('bulkDonorScheduling.Validate')
              )}
            </Button>
          )}

          <Button
            size="xl"
            className="justify-center"
            disabled={
              state.loading || state.type === 'success' || isGridEditing
            }
            onClick={() => {
              dispatch({ type: 'add_rows', count: 10 });
            }}
          >
            {t('bulkDonorScheduling.Add_Rows')}
          </Button>

          <Button
            size="xl"
            className="justify-center"
            variant="destructive"
            disabled={state.loading || isGridEditing}
            onClick={() => {
              setDonorResolutions({});
              setPendingDonorMismatches([]);
              dispatch({ type: 'reset' });
            }}
          >
            {t('bulkDonorScheduling.Reset')}
          </Button>
        </div>
      </div>

      <BulkDonorScheduleGrid
        columns={columns}
        onEditingChange={setGridEditing}
        onReady={setApi}
        onRowChanged={handleRowChanged}
        state={state}
      />
    </div>
  );
}
